/**
 * Построение `Pattern` из среза MIDI. Если целевой слот занят — возвращает
 * конфликт (UI показывает confirm-dialog «Перезаписать?»).
 *
 * Имя по умолчанию: `${fileBaseName} T{track:02d}-{slot}`.
 * Длина по умолчанию = `range.endMeasure - range.startMeasure` (не более 64).
 *
 * Time-signature-aware: конвертация мер → тиков использует
 * `measureBoundaries` из `time.ts`, который учитывает смены сигнатуры
 * внутри файла. Не полагаемся на одну `ticksPerMeasure(timeSigs[0])` —
 * иначе срез, пересекающий 4/4 → 3/4, выйдет со смещёнными нотами.
 */

import {
  type Midi,
  type MidiChannel,
  type MidiNote,
} from "@midi-fantom-utils/midi-core";
import { sliceMidi } from "./slice.js";
import {
  type Pattern,
  type PatternBuildResult,
  type PatternId,
  type PatternSlot,
  type SliceRange,
  type TrackNumber,
  formatPatternId,
} from "./types.js";
import { measureBoundaries } from "./time.js";

export const FANTOM_MAX_PATTERN_LENGTH_MEASURES = 64;

export type BuildPatternOptions = {
  fileBaseName: string;
  existing: ReadonlyMap<PatternId, Pattern>;
  name?: string;
  lengthMeasures?: number;
  channel: MidiChannel;
};

const computeTotalTicks = (midi: Midi): number => {
  let max = 0;
  for (const t of midi.tracks) {
    for (const n of t.notes) {
      const end = n.ticks + n.durationTicks;
      if (end > max) max = end;
    }
  }
  // Берём хотя бы один такт вперёд, чтобы measureBoundaries мог
  // вычислить границу конца последнего полного такта.
  return Math.max(max, midi.header.ppq * 4);
};

const rangeToTicks = (
  midi: Midi,
  range: SliceRange
): { startTick: number; endTick: number } => {
  const totalTicks = computeTotalTicks(midi);
  const boundaries = measureBoundaries(
    midi.header.ppq,
    midi.header.timeSignatures,
    totalTicks
  );
  const startMeasure = Math.max(1, Math.min(boundaries.length, range.startMeasure));
  const endMeasure = Math.max(
    startMeasure + 1,
    Math.min(boundaries.length, range.endMeasure)
  );
  return {
    startTick: boundaries[startMeasure - 1] ?? 0,
    endTick: boundaries[endMeasure - 1] ?? totalTicks,
  };
};

export const buildPattern = (
  source: Midi,
  track: TrackNumber,
  slot: PatternSlot,
  range: SliceRange,
  options: BuildPatternOptions
):
  | PatternBuildResult
  | { ok: false; reason: "empty" }
  | { ok: false; reason: "too_long" } => {
  const { startTick, endTick } = rangeToTicks(source, range);
  if (startTick >= endTick) {
    return { ok: false, reason: "empty" };
  }
  const slice = sliceMidi(source, startTick, endTick, options.channel);
  if (!slice || slice.noteCount === 0) {
    return { ok: false, reason: "empty" };
  }

  const id = formatPatternId(track, slot);
  const existingPattern = options.existing.get(id);
  if (existingPattern) {
    return { ok: false, conflict: { existing: existingPattern } };
  }

  const safeTrack = (track >= 1 && track <= 16 ? track : 1) as TrackNumber;
  const defaultName = `${options.fileBaseName} T${String(safeTrack).padStart(2, "0")}-${slot}`;
  const name = options.name?.trim() || defaultName;
  const defaultLength = Math.max(1, range.endMeasure - range.startMeasure);
  const requestedLength = options.lengthMeasures ?? defaultLength;
  const safeRequested =
    Number.isFinite(requestedLength) && requestedLength > 0
      ? Math.round(requestedLength)
      : 1;
  const length = Math.max(
    1,
    Math.min(FANTOM_MAX_PATTERN_LENGTH_MEASURES, safeRequested)
  );

  const trimmed = trimMidiToLengthTicks(source, slice.midi, length);

  const pattern: Pattern = {
    id,
    track: safeTrack,
    slot,
    name,
    lengthMeasures: length,
    midi: trimmed,
    sourceChannel: options.channel,
    noteCount: countNotes(trimmed),
  };
  return { ok: true, pattern };
};

/**
 * Обрезает MIDI до `lengthMeasures` тактов. Использует
 * `measureBoundaries` для точного TS-aware расчёта `endTick`.
 * Срез, охватывающий несколько сигнатур, корректно обрезается на
 * границе нужного такта.
 */
const trimMidiToLengthTicks = (
  source: Midi,
  midi: Midi,
  lengthMeasures: number
): Midi => {
  if (lengthMeasures <= 0) return midi;
  const totalTicks = computeTotalTicks(source);
  const boundaries = measureBoundaries(
    source.header.ppq,
    source.header.timeSignatures,
    totalTicks
  );
  // `lengthMeasures` — желаемая длина результата в тактах. Берём
  // границу конца такта `lengthMeasures` (1-based: 1 такт → boundaries[1],
  // 2 такта → boundaries[2]). Клампим к доступному диапазону.
  const idx = Math.min(boundaries.length - 1, Math.max(0, lengthMeasures));
  const endTick = boundaries[idx] ?? totalTicks;
  const tracks = midi.tracks.map((t) => {
    const newNotes: MidiNote[] = [];
    for (const n of t.notes) {
      if (n.ticks >= endTick) continue;
      const noteEnd = n.ticks + n.durationTicks;
      const trimmedEnd = Math.min(noteEnd, endTick);
      if (trimmedEnd <= n.ticks) continue;
      newNotes.push({ ...n, durationTicks: trimmedEnd - n.ticks });
    }
    return { ...t, notes: newNotes };
  });
  return { ...midi, tracks };
};

export const countNotes = (midi: Midi): number => {
  let n = 0;
  for (const t of midi.tracks) n += t.notes.length;
  return n;
};
